import { api, parseApiError } from './api';
import type { Pot, PotsListResponse, CreatePotInput, UpdatePotInput } from '@/types/pot';

// API response type (raw from backend)
interface ApiPot {
  id: string;
  name: string;
  description?: string | null;
  color: string;
  icon?: string | null;
  currency: 'BRL' | 'GBP' | 'USD' | 'EUR';
  budget?: number | null;
  isDefault: boolean;
  totalExpenses?: number | string;
  totalIncome?: number | string;
  transactionCount: number;
  budgetRemaining?: number | null;
}

interface ApiPotsResponse {
  pots: ApiPot[];
}

// Transform API pot to frontend Pot type
function transformPot(apiPot: ApiPot): Pot {
  const totalIncome = typeof apiPot.totalIncome === 'string'
    ? parseFloat(apiPot.totalIncome)
    : (apiPot.totalIncome || 0);
  const totalExpenses = typeof apiPot.totalExpenses === 'string'
    ? parseFloat(apiPot.totalExpenses)
    : (apiPot.totalExpenses || 0);

  return {
    id: apiPot.id,
    name: apiPot.name,
    description: apiPot.description,
    currency: apiPot.currency,
    color: apiPot.color,
    icon: apiPot.icon,
    isDefault: apiPot.isDefault,
    transactionCount: apiPot.transactionCount,
    budget: apiPot.budget,
    budgetRemaining: apiPot.budgetRemaining,
    totalIncome,
    totalExpenses,
    balance: totalIncome - totalExpenses,
  };
}

export const potsService = {
  /**
   * Get all pots for the authenticated user
   */
  async getPots(): Promise<PotsListResponse> {
    try {
      const response = await api.get<ApiPotsResponse>('/api/pots');
      return {
        pots: response.data.pots.map(transformPot),
        total: response.data.pots.length,
        totalBalance: {},
      };
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Get a single pot by ID
   */
  async getPot(id: string): Promise<Pot> {
    try {
      const response = await api.get<Pot>(`/api/pots/${id}`);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Create a new pot
   */
  async createPot(data: CreatePotInput): Promise<Pot> {
    try {
      const response = await api.post<Pot>('/api/pots', data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Update a pot
   */
  async updatePot(id: string, data: UpdatePotInput): Promise<Pot> {
    try {
      const response = await api.patch<Pot>(`/api/pots/${id}`, data);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Delete a pot
   */
  async deletePot(id: string): Promise<void> {
    try {
      await api.delete(`/api/pots/${id}`);
    } catch (error) {
      throw parseApiError(error);
    }
  },

  /**
   * Set a pot as default
   */
  async setDefaultPot(id: string): Promise<Pot> {
    try {
      const response = await api.post<Pot>(`/api/pots/${id}/default`);
      return response.data;
    } catch (error) {
      throw parseApiError(error);
    }
  },
};

export default potsService;
